import React, { useState, useEffect, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiClock,
  FiCheck,
  FiX,
  FiTarget,
  FiBookOpen,
  FiBookmark,
} from 'react-icons/fi';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import { saveConceptsLessonCompletion } from '../../ConceptsMastery';
import LessonNavigation from './LessonNavigation';
import GetStartedWith from '../../../../../components/GetStartedWith';
import { setLocalStorageWithSync } from '../../../../../utils/utils';
import {
  QC_OPTIONS,
  getGreQuantCorrectAnswerText,
  getGreQuantQuestionType,
  getGreQuantUserAnswerText,
  isGreQuantAnswerCorrect,
  isGreQuantQuestionAnswered,
} from '../../quantAnswerUtils';
import { renderGreMathText as renderMathText } from '../../../../../utils/renderGreMath';
import { applyLessonStrategyTips } from '../strategyDistributionMap';
import { applyLessonScaffolding } from '../lessonScaffoldingMap';
import Sidebar from '../../../../Sidebar';
import '../QuantLessonMobile.css';

/**
 * LessonTemplate - Composant réutilisable pour toutes les leçons GRE Quant
 *
 * @param {Object} props
 * @param {string} props.lessonId - ID unique de la leçon (ex: 'algebra', 'circles')
 * @param {string} props.lessonTitle - Titre de la leçon (ex: 'Algebra', 'Circles')
 * @param {JSX.Element} props.lessonIcon - Icône de la leçon (ex: <FiActivity />)
 * @param {string} props.lessonDescription - Description courte de la leçon
 * @param {Array} props.sections - Tableau des sections de la leçon
 * @param {Array} props.practiceQuestions - Tableau des questions de pratique
 * @param {Array} props.quickReference - Tableau des références rapides (strings)
 * @param {Object} props.getStartedWithProps - Props pour le composant GetStartedWith
 * @param {React.ReactNode} [props.supplementalPracticeSlot] - CTA optionnel (ex. lien vers la banque Quant)
 */
const LessonTemplate = ({
  lessonId,
  lessonTitle,
  lessonIcon,
  lessonDescription,
  sections,
  practiceQuestions,
  quickReference,
  getStartedWithProps,
  supplementalPracticeSlot,
}) => {
  const navigate = useNavigate();
  const enrichedSections = applyLessonStrategyTips(
    lessonId,
    applyLessonScaffolding(lessonId, sections)
  );
  const [currentSection, setCurrentSection] = useState(0);
  const [showPractice, setShowPractice] = useState(false);
  const [practiceMode, setPracticeMode] = useState('timed');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [practiceStartTime, setPracticeStartTime] = useState(null);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [isConceptSaved, setIsConceptSaved] = useState(false);
  const timedCheckCount = practiceQuestions.filter(
    (q) => q.type === 'timed'
  ).length;
  const formatCheckCount = (count, suffix) =>
    `${count} quick check${count === 1 ? '' : 's'}, ${suffix}`;

  const renderLessonLayout = (children) => (
    <div className="layout">
      <Sidebar />
      <main className="content">{children}</main>
    </div>
  );

  // Load saved concept status from localStorage
  useEffect(() => {
    const savedConcepts =
      JSON.parse(localStorage.getItem('savedConcepts')) || [];
    setIsConceptSaved(savedConcepts.some((c) => c.lessonId === lessonId));
  }, [lessonId]);

  // Toggle save concept
  const toggleSaveConcept = async () => {
    const savedConcepts =
      JSON.parse(localStorage.getItem('savedConcepts')) || [];

    if (isConceptSaved) {
      const updatedConcepts = savedConcepts.filter(
        (c) => c.lessonId !== lessonId
      );
      await setLocalStorageWithSync(
        'savedConcepts',
        JSON.stringify(updatedConcepts)
      );
      setIsConceptSaved(false);
    } else {
      const conceptData = {
        lessonId: lessonId,
        lessonTitle: lessonTitle,
        quickReference: quickReference,
        savedAt: new Date().toISOString(),
      };
      savedConcepts.push(conceptData);
      await setLocalStorageWithSync(
        'savedConcepts',
        JSON.stringify(savedConcepts)
      );
      setIsConceptSaved(true);
    }
  };

  // Timer effect
  useEffect(() => {
    let interval = null;
    if (isTimerActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0 && isTimerActive) {
      handleTimeUp();
    }
    return () => clearInterval(interval);
  }, [isTimerActive, timeLeft]);

  const handleTimeUp = () => {
    setIsTimerActive(false);
    if (currentQuestion < getFilteredQuestions().length - 1) {
      const nextIndex = currentQuestion + 1;
      setTimeout(() => {
        setCurrentQuestion(nextIndex);
        startTimer(nextIndex);
      }, 1000);
    } else {
      finishPractice();
    }
  };

  const startTimer = (questionIndex = currentQuestion, mode = practiceMode) => {
    if (mode === 'timed') {
      const questions = practiceQuestions.filter((q) => q.type === 'timed');
      const timeLimit = questions[questionIndex]?.timeLimit || 90;
      setTimeLeft(timeLimit);
      setIsTimerActive(true);
    }
  };

  const getFilteredQuestions = () => {
    return practiceQuestions.filter((q) => q.type === practiceMode);
  };

  const handleAnswerSelect = (question, answerIndex) => {
    const questionType = getGreQuantQuestionType(question);

    if (questionType === 'multiple_choice_multi') {
      const currentAnswer = Array.isArray(selectedAnswers[currentQuestion])
        ? selectedAnswers[currentQuestion]
        : [];
      const nextAnswer = currentAnswer.includes(answerIndex)
        ? currentAnswer.filter((index) => index !== answerIndex)
        : [...currentAnswer, answerIndex];

      setSelectedAnswers({
        ...selectedAnswers,
        [currentQuestion]: nextAnswer,
      });
      return;
    }

    setSelectedAnswers({
      ...selectedAnswers,
      [currentQuestion]: answerIndex,
    });
  };

  const handleNumericAnswerChange = (value) => {
    setSelectedAnswers({
      ...selectedAnswers,
      [currentQuestion]: value,
    });
  };

  const handleNextQuestion = () => {
    const questions = getFilteredQuestions();
    if (currentQuestion < questions.length - 1) {
      const nextIndex = currentQuestion + 1;
      setCurrentQuestion(nextIndex);
      if (practiceMode === 'timed') {
        startTimer(nextIndex);
      }
    } else {
      finishPractice();
    }
  };

  const finishPractice = async () => {
    setIsTimerActive(false);
    setShowResults(true);

    const questions = getFilteredQuestions();
    const correct = questions.reduce(
      (acc, question, index) =>
        acc +
        (isGreQuantAnswerCorrect(question, selectedAnswers[index]) ? 1 : 0),
      0
    );

    const score = Math.round((correct / questions.length) * 100);
    const timeSpent = Date.now() - practiceStartTime;

    await saveConceptsLessonCompletion(
      {
        lessonId: lessonId,
        score: score,
        timeSpent: timeSpent,
        totalQuestions: questions.length,
        practiceMode: practiceMode,
        sessionStartTime: new Date(practiceStartTime).toISOString(),
      },
      null
    );
  };

  const startPractice = (mode) => {
    setPracticeMode(mode);
    setShowPractice(true);
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setShowResults(false);
    setPracticeStartTime(Date.now());

    if (mode === 'timed') {
      startTimer(0, mode);
    }
  };

  const handleExitPractice = () => {
    if (practiceMode === 'timed' && isTimerActive && !showResults) {
      setShowExitConfirmation(true);
    } else {
      confirmExitPractice();
    }
  };

  const confirmExitPractice = () => {
    setIsTimerActive(false);
    setShowPractice(false);
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setTimeLeft(0);
    setShowResults(false);
    setShowExitConfirmation(false);
  };

  const cancelExitPractice = () => {
    setShowExitConfirmation(false);
  };

  const renderTimer = () => {
    if (practiceMode !== 'timed' || !isTimerActive) return null;

    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;
    const isWarning = timeLeft <= 30;

    return (
      <div
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: isWarning ? '#fee2e2' : '#f0f9ff',
          border: `2px solid ${isWarning ? '#ef4444' : '#3b82f6'}`,
          borderRadius: '12px',
          padding: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          zIndex: 1000,
          color: isWarning ? '#dc2626' : '#1d4ed8',
          fontWeight: 'bold',
        }}
      >
        <FiClock style={{ marginRight: '0.5rem' }} />
        {minutes}:{seconds.toString().padStart(2, '0')}
      </div>
    );
  };

  const renderExitConfirmationModal = () => {
    if (!showExitConfirmation) return null;

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
        }}
      >
        <div
          style={{
            background: 'var(--liwaza-surface-container)',
            padding: '2rem',
            borderRadius: '12px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              color: 'var(--liwaza-error)',
              fontSize: '3rem',
              marginBottom: '1rem',
            }}
          >
            ⚠️
          </div>

          <h3
            style={{
              color: 'var(--liwaza-on-surface)',
              marginBottom: '1rem',
            }}
          >
            Exit Timed Practice?
          </h3>

          <p
            style={{
              color: 'var(--liwaza-on-surface-variant)',
              marginBottom: '2rem',
              lineHeight: 1.5,
            }}
          >
            Are you sure you want to exit? Your progress in this timed practice
            session will be lost.
          </p>

          <div
            style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
            }}
          >
            <button
              onClick={cancelExitPractice}
              style={{
                background: 'var(--liwaza-secondary)',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              Continue Practice
            </button>

            <button
              onClick={confirmExitPractice}
              style={{
                background: 'var(--liwaza-error)',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              Exit Practice
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderResults = () => {
    const questions = getFilteredQuestions();
    const correct = questions.reduce(
      (acc, question, index) =>
        acc +
        (isGreQuantAnswerCorrect(question, selectedAnswers[index]) ? 1 : 0),
      0
    );
    const score = Math.round((correct / questions.length) * 100);

    return (
      <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        <h2 style={{ textAlign: 'center', marginBottom: '2rem' }}>
          Practice Results
        </h2>

        <div
          style={{
            background: score >= 70 ? '#dcfce7' : '#fef2f2',
            border: `1px solid ${score >= 70 ? '#16a34a' : '#dc2626'}`,
            borderRadius: '8px',
            padding: '2rem',
            textAlign: 'center',
            marginBottom: '2rem',
          }}
        >
          <h3
            style={{
              color: score >= 70 ? '#16a34a' : '#dc2626',
              marginBottom: '1rem',
            }}
          >
            {score >= 70 ? '🎉 Great Job!' : '📚 Keep Practicing!'}
          </h3>
          <p
            style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              marginBottom: '0.5rem',
            }}
          >
            {correct}/{questions.length} correct ({score}%)
          </p>
          <p style={{ color: 'var(--liwaza-on-surface-variant)' }}>
            {score >= 90
              ? 'Excellent mastery!'
              : score >= 70
                ? 'Good understanding!'
                : 'Review the concepts and try again.'}
          </p>
        </div>

        {questions.map((question, index) => {
          const isCorrect = isGreQuantAnswerCorrect(
            question,
            selectedAnswers[index]
          );

          return (
            <div
              key={index}
              style={{
                background: 'var(--liwaza-surface-container)',
                padding: '1.5rem',
                borderRadius: '8px',
                marginBottom: '1rem',
                border: `2px solid ${isCorrect ? '#16a34a' : '#dc2626'}`,
              }}
            >
              <p style={{ fontWeight: 'bold', marginBottom: '1rem' }}>
                {index + 1}. {renderMathText(question.question)}
              </p>
              <p style={{ marginBottom: '0.5rem' }}>
                <strong>Your answer:</strong>{' '}
                {renderMathText(
                  getGreQuantUserAnswerText(question, selectedAnswers[index])
                )}
              </p>
              <p style={{ marginBottom: '0.5rem' }}>
                <strong>Correct answer:</strong>{' '}
                {renderMathText(getGreQuantCorrectAnswerText(question))}
              </p>
              <p
                style={{
                  color: 'var(--liwaza-on-surface-variant)',
                  fontSize: '0.9rem',
                }}
              >
                {renderMathText(question.explanation)}
              </p>
            </div>
          );
        })}

        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <button
            onClick={() => setShowPractice(false)}
            style={{
              background: 'var(--liwaza-primary)',
              color: 'white',
              border: 'none',
              padding: '1rem 2rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem',
              marginRight: '1rem',
            }}
          >
            Back to Lesson
          </button>
          <button
            onClick={() => {
              setShowResults(false);
              setCurrentQuestion(0);
              setSelectedAnswers({});
              if (practiceMode === 'timed') startTimer(0);
            }}
            style={{
              background: 'var(--liwaza-secondary)',
              color: 'white',
              border: 'none',
              padding: '1rem 2rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem',
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  };

  const renderPractice = () => {
    if (showResults) return renderResults();

    const questions = getFilteredQuestions();
    const question = questions[currentQuestion];
    const questionType = getGreQuantQuestionType(question);
    const selectedAnswer = selectedAnswers[currentQuestion];
    const answerOptions =
      questionType === 'quantitative_comparison'
        ? QC_OPTIONS
        : question.options || [];

    return (
      <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
          <h2>
            Practice Question {currentQuestion + 1} of {questions.length}
          </h2>
          <div
            style={{
              background: 'var(--liwaza-primary)',
              height: '4px',
              borderRadius: '2px',
              marginTop: '1rem',
            }}
          >
            <div
              style={{
                background: 'var(--liwaza-secondary)',
                height: '100%',
                borderRadius: '2px',
                width: `${((currentQuestion + 1) / questions.length) * 100}%`,
                transition: 'width 0.3s ease',
              }}
            />
          </div>
        </div>

        <div
          style={{
            background: 'var(--liwaza-surface-container)',
            padding: '2rem',
            borderRadius: '12px',
            marginBottom: '2rem',
          }}
        >
          <h3 style={{ marginBottom: '1.5rem', lineHeight: 1.4 }}>
            {renderMathText(question.question)}
          </h3>

          {questionType === 'quantitative_comparison' ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '1rem',
                marginBottom: '1.25rem',
              }}
            >
              <div
                style={{
                  background: 'var(--liwaza-surface)',
                  border: '1px solid var(--liwaza-outline)',
                  borderRadius: '8px',
                  padding: '1rem',
                }}
              >
                <strong>Quantity A</strong>
                <div style={{ marginTop: '0.5rem' }}>
                  {renderMathText(question.quantityA)}
                </div>
              </div>
              <div
                style={{
                  background: 'var(--liwaza-surface)',
                  border: '1px solid var(--liwaza-outline)',
                  borderRadius: '8px',
                  padding: '1rem',
                }}
              >
                <strong>Quantity B</strong>
                <div style={{ marginTop: '0.5rem' }}>
                  {renderMathText(question.quantityB)}
                </div>
              </div>
            </div>
          ) : null}

          {questionType === 'numeric_entry' ? (
            <input
              type="text"
              value={selectedAnswer || ''}
              onChange={(event) =>
                handleNumericAnswerChange(event.target.value)
              }
              placeholder="Enter an integer, decimal, or fraction"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '1rem',
                border: '2px solid var(--liwaza-outline)',
                borderRadius: '8px',
                background: 'var(--liwaza-surface)',
                color: 'var(--liwaza-on-surface)',
                fontSize: '1rem',
              }}
            />
          ) : (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {answerOptions.map((option, index) => {
                const isSelected =
                  questionType === 'multiple_choice_multi'
                    ? Array.isArray(selectedAnswer) &&
                      selectedAnswer.includes(index)
                    : selectedAnswer === index;

                return (
                  <button
                    key={index}
                    onClick={() => handleAnswerSelect(question, index)}
                    style={{
                      padding: '1rem',
                      border: '2px solid',
                      borderColor: isSelected
                        ? 'var(--liwaza-primary)'
                        : 'var(--liwaza-outline)',
                      borderRadius: '8px',
                      background: isSelected
                        ? 'var(--liwaza-primary-container)'
                        : 'var(--liwaza-surface)',
                      color: 'var(--liwaza-on-surface)',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '1rem',
                    }}
                  >
                    {questionType === 'multiple_choice_multi'
                      ? isSelected
                        ? '[x] '
                        : '[ ] '
                      : null}
                    {String.fromCharCode(65 + index)}. {renderMathText(option)}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div
          className="gre-quant-lesson-practice-footer"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <button
            onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
            disabled={currentQuestion === 0}
            style={{
              background:
                currentQuestion === 0
                  ? 'var(--liwaza-surface-container)'
                  : 'var(--liwaza-secondary)',
              color:
                currentQuestion === 0
                  ? 'var(--liwaza-on-surface-variant)'
                  : 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              cursor: currentQuestion === 0 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <FiArrowLeft /> Previous
          </button>

          <span style={{ color: 'var(--liwaza-on-surface-variant)' }}>
            {currentQuestion + 1} / {questions.length}
          </span>

          <button
            onClick={handleNextQuestion}
            disabled={!isGreQuantQuestionAnswered(question, selectedAnswer)}
            style={{
              background: isGreQuantQuestionAnswered(question, selectedAnswer)
                ? 'var(--liwaza-primary)'
                : 'var(--liwaza-surface-container)',
              color: isGreQuantQuestionAnswered(question, selectedAnswer)
                ? 'white'
                : 'var(--liwaza-on-surface-variant)',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              cursor: isGreQuantQuestionAnswered(question, selectedAnswer)
                ? 'pointer'
                : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            {currentQuestion === questions.length - 1 ? (
              <>
                Finish <FiCheck />
              </>
            ) : (
              <>
                Next <FiArrowLeft style={{ transform: 'rotate(180deg)' }} />
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  if (showPractice) {
    return renderLessonLayout(
      <>
        {renderTimer()}
        <div style={{ padding: '2rem' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '2rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <h2
                style={{
                  color: 'var(--liwaza-on-surface)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  margin: 0,
                }}
              >
                {practiceMode === 'timed' ? <FiClock /> : <FiBookOpen />}
                {practiceMode === 'timed'
                  ? 'Timed Practice'
                  : 'Untimed Practice'}
              </h2>

              <button
                onClick={handleExitPractice}
                style={{
                  background: 'var(--liwaza-surface-container)',
                  color: 'var(--liwaza-on-surface)',
                  border: '2px solid var(--liwaza-outline)',
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.9rem',
                }}
              >
                <FiArrowLeft /> Exit Practice
              </button>
            </div>
          </div>

          {renderPractice()}
        </div>
        {renderExitConfirmationModal()}
      </>
    );
  }

  const currentSectionData = enrichedSections[currentSection];

  return renderLessonLayout(
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <button
          onClick={() =>
            navigate('/exams/gre', { state: { activeCategory: 'concepts' } })
          }
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--liwaza-primary)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '1rem',
            marginBottom: '1rem',
          }}
        >
          <FiArrowLeft /> Back to Concepts
        </button>

        <h1
          style={{
            color: 'var(--liwaza-on-surface)',
            marginBottom: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
          }}
        >
          {lessonIcon}
          {lessonTitle}
        </h1>
        <p
          style={{
            color: 'var(--liwaza-on-surface-variant)',
            fontSize: '1.1rem',
          }}
        >
          {lessonDescription}
        </p>
      </div>

      {/* Section Navigation */}
      <div
        style={{
          display: 'flex',
          gap: '1rem',
          marginBottom: '2rem',
          flexWrap: 'wrap',
        }}
      >
        {enrichedSections.map((section, index) => (
          <button
            key={section.id}
            onClick={() => setCurrentSection(index)}
            style={{
              padding: '0.75rem 1.5rem',
              border: 'none',
              borderRadius: '8px',
              background:
                currentSection === index
                  ? 'var(--liwaza-primary)'
                  : 'var(--liwaza-surface-container)',
              color:
                currentSection === index ? 'white' : 'var(--liwaza-on-surface)',
              cursor: 'pointer',
              fontWeight: currentSection === index ? 600 : 400,
            }}
          >
            {section.title}
          </button>
        ))}
      </div>

      {/* Main Content */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 300px',
          gap: '2rem',
          alignItems: 'start',
        }}
      >
        {/* Content Area */}
        <div>
          <div
            style={{
              background: 'var(--liwaza-surface-container)',
              padding: '2rem',
              borderRadius: '12px',
              marginBottom: '2rem',
            }}
          >
            <h2 style={{ marginBottom: '1rem' }}>{currentSectionData.title}</h2>
            <p
              style={{
                fontSize: '1.1rem',
                lineHeight: 1.6,
                color: 'var(--liwaza-on-surface-variant)',
                marginBottom: '2rem',
                whiteSpace: 'pre-line',
                overflowWrap: 'anywhere',
              }}
            >
              {renderMathText(currentSectionData.content.introduction)}
            </p>

            {currentSectionData.content.concepts.map((concept, index) => (
              <div
                key={index}
                style={{
                  background: 'var(--liwaza-surface-container-highest)',
                  padding: '1.5rem',
                  borderRadius: '8px',
                  marginBottom: '1.5rem',
                }}
              >
                <h3
                  style={{
                    color: 'var(--liwaza-primary)',
                    marginBottom: '1rem',
                  }}
                >
                  {renderMathText(concept.title)}
                </h3>
                {concept.prerequisiteNote ? (
                  <div
                    style={{
                      marginBottom: '1rem',
                      padding: '0.85rem 1rem',
                      borderRadius: '8px',
                      background: 'var(--liwaza-surface-container)',
                      border: '1px solid var(--liwaza-outline-variant)',
                      color: 'var(--liwaza-on-surface-variant)',
                      lineHeight: 1.5,
                    }}
                  >
                    <strong>Before this:</strong>{' '}
                    {renderMathText(concept.prerequisiteNote)}
                  </div>
                ) : null}
                {Array.isArray(concept.vocabulary) &&
                concept.vocabulary.length > 0 ? (
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '0.5rem',
                      marginBottom: '1rem',
                    }}
                  >
                    {concept.vocabulary.map((term) => (
                      <span
                        key={term}
                        style={{
                          padding: '0.35rem 0.65rem',
                          borderRadius: '999px',
                          background: 'var(--liwaza-surface-container)',
                          border: '1px solid var(--liwaza-outline-variant)',
                          color: 'var(--liwaza-on-surface-variant)',
                          fontSize: '0.85rem',
                        }}
                      >
                        {renderMathText(term)}
                      </span>
                    ))}
                  </div>
                ) : null}
                <p
                  style={{
                    marginBottom: '1.5rem',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {renderMathText(concept.explanation)}
                </p>

                {concept.microExample?.problem ? (
                  <div
                    style={{
                      marginBottom: '1rem',
                      padding: '1rem',
                      borderRadius: '8px',
                      background: 'var(--liwaza-surface-container)',
                    }}
                  >
                    <strong>Micro-example:</strong>
                    <div style={{ marginTop: '0.5rem', lineHeight: 1.6 }}>
                      {renderMathText(concept.microExample.problem)}
                    </div>
                    {Array.isArray(concept.microExample.solution) ? (
                      <ol style={{ marginTop: '0.5rem', paddingLeft: '1.25rem' }}>
                        {concept.microExample.solution.map((step, stepIndex) => (
                          <li key={stepIndex} style={{ margin: '0.35rem 0' }}>
                            {renderMathText(step)}
                          </li>
                        ))}
                      </ol>
                    ) : null}
                  </div>
                ) : null}

                {concept.greTrap ? (
                  <div
                    style={{
                      marginBottom: '1rem',
                      padding: '0.85rem 1rem',
                      borderRadius: '8px',
                      background: 'var(--liwaza-error-container)',
                      color: 'var(--liwaza-on-error-container)',
                      lineHeight: 1.5,
                    }}
                  >
                    <strong>GRE trap:</strong> {renderMathText(concept.greTrap)}
                  </div>
                ) : null}

                {concept.timingTip ? (
                  <div
                    style={{
                      marginBottom: '1rem',
                      padding: '0.85rem 1rem',
                      borderRadius: '8px',
                      background: 'var(--liwaza-surface-container-high)',
                      color: 'var(--liwaza-on-surface)',
                      border: '1px solid var(--liwaza-outline-variant)',
                      lineHeight: 1.5,
                    }}
                  >
                    <strong>
                      <FiClock style={{ verticalAlign: 'middle' }} /> Timing:
                    </strong>{' '}
                    {renderMathText(concept.timingTip)}
                  </div>
                ) : null}

                {concept.strategyTip ? (
                  <div
                    style={{
                      marginBottom: '1rem',
                      padding: '0.85rem 1rem',
                      borderRadius: '8px',
                      background: 'var(--liwaza-primary-container)',
                      color: 'var(--liwaza-on-primary-container)',
                      lineHeight: 1.5,
                    }}
                  >
                    <strong>
                      <FiTarget style={{ verticalAlign: 'middle' }} /> Strategy:
                    </strong>{' '}
                    {renderMathText(concept.strategyTip)}
                  </div>
                ) : null}

                {concept.checkQuestion ? (
                  <div
                    style={{
                      marginBottom: '1.5rem',
                      padding: '0.85rem 1rem',
                      borderRadius: '8px',
                      background: 'var(--liwaza-secondary-container)',
                      color: 'var(--liwaza-on-secondary-container)',
                      lineHeight: 1.5,
                    }}
                  >
                    <strong>Quick check:</strong>{' '}
                    {renderMathText(concept.checkQuestion)}
                  </div>
                ) : null}

                {/* Visual Component (if exists) */}
                {concept.visual && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      marginBottom: '1.5rem',
                    }}
                  >
                    <Suspense fallback={<div>Loading diagram...</div>}>
                      {concept.visual}
                    </Suspense>
                  </div>
                )}

                {concept.example?.problem ? (
                  <div
                    style={{
                      background: 'var(--liwaza-primary-container)',
                      padding: '1.5rem',
                      borderRadius: '8px',
                      border: '1px solid var(--liwaza-primary)',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        marginBottom: '1rem',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.75rem',
                      }}
                    >
                      <div
                        style={{
                          color: 'var(--liwaza-primary)',
                          fontWeight: 700,
                          fontSize: '1.05rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                        }}
                      >
                        <FiTarget />
                        <span>Example</span>
                      </div>
                      <div
                        style={{
                          color: 'var(--liwaza-on-primary-container)',
                          fontSize: '1.1rem',
                          lineHeight: 1.5,
                          overflowX: 'auto',
                          overflowY: 'hidden',
                          paddingBottom: '0.25rem',
                        }}
                      >
                        {renderMathText(concept.example.problem)}
                      </div>
                    </div>
                    {Array.isArray(concept.example.solution) ? (
                      <div style={{ color: 'var(--liwaza-on-primary-container)' }}>
                        <strong>Solution:</strong>
                        <ol
                          style={{
                            marginTop: '0.5rem',
                            marginLeft: '1rem',
                            paddingLeft: '0.75rem',
                          }}
                        >
                          {concept.example.solution.map((step, stepIndex) => (
                            <li
                              key={stepIndex}
                              style={{
                                margin: '0.5rem 0',
                                lineHeight: 1.6,
                                overflowWrap: 'anywhere',
                              }}
                            >
                              <span
                                style={{
                                  display: 'inline-block',
                                  maxWidth: '100%',
                                  overflowX: 'auto',
                                  verticalAlign: 'top',
                                }}
                              >
                                {renderMathText(step)}
                              </span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>

        {/* Sidebar */}
        <div
          style={{
            background: 'var(--liwaza-surface-container)',
            padding: '1.5rem',
            borderRadius: '12px',
            position: 'sticky',
            top: '2rem',
          }}
        >
          <h3
            style={{
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <FiBookOpen />
            Practice
          </h3>

          {/* Timed Lesson Check Button */}
          <div style={{ marginBottom: '1.5rem' }}>
            <button
              onClick={() => startPractice('timed')}
              style={{
                width: '100%',
                padding: '1rem',
                border: 'none',
                borderRadius: '8px',
                background: 'var(--liwaza-primary)',
                color: 'white',
                cursor: 'pointer',
                marginBottom: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                justifyContent: 'center',
              }}
            >
              <FiClock />
              Timed Check
            </button>
            <p
              style={{
                fontSize: '0.9rem',
                color: 'var(--liwaza-on-surface-variant)',
                textAlign: 'center',
              }}
            >
              {formatCheckCount(timedCheckCount, 'GRE pacing')}
            </p>
          </div>

          {supplementalPracticeSlot ? (
            <div style={{ marginBottom: '1.5rem' }}>
              {supplementalPracticeSlot}
            </div>
          ) : null}

          {/* Quick Reference */}
          <div
            style={{
              padding: '1rem',
              background: 'var(--liwaza-surface-container-highest)',
              borderRadius: '8px',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.5rem',
              }}
            >
              <h4 style={{ margin: 0 }}>Quick Reference</h4>
              <button
                onClick={toggleSaveConcept}
                style={{
                  background: isConceptSaved
                    ? 'var(--liwaza-primary)'
                    : 'var(--liwaza-secondary-container)',
                  color: isConceptSaved
                    ? 'white'
                    : 'var(--liwaza-on-secondary-container)',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.85rem',
                  transition: 'all 0.2s',
                }}
              >
                <FiBookmark size={14} />
                {isConceptSaved ? 'Saved' : 'Save Concept'}
              </button>
            </div>
            <ul
              style={{
                fontSize: '0.9rem',
                color: 'var(--liwaza-on-surface-variant)',
                listStyle: 'none',
                padding: 0,
                marginTop: '0.75rem',
              }}
            >
              {quickReference.map((item, index) => (
                <li key={index}>• {renderMathText(item)}</li>
              ))}
            </ul>
          </div>

          {/* Get Started With Component */}
          {getStartedWithProps && (
            <div style={{ marginTop: '1.5rem' }}>
              <GetStartedWith {...getStartedWithProps} />
            </div>
          )}
        </div>
      </div>

      {/* Lesson Navigation */}
      <LessonNavigation currentLessonId={lessonId} />
    </div>
  );
};

export default LessonTemplate;
